/*
 * Project: bellcloud
 * File: apps/frontend/src/components/configuration/SensorConfigCard.tsx
 * Author: SWAN
 * Created: 2025-08-06 16:30:00
 * Description: Sensor Configuration Card Component
 */
import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { media } from '@/theme/tokens/breakpoints';
import { useTranslation } from 'react-i18next';
import { Card as BaseCard, CardTitle } from '@/components/ui';
import { NumberInput } from '@/components/ui/common/input/NumberInput';
import { Toggle } from '@/components/ui/common/input/Toggle';
import { SelectBox } from '@/components/ui/common/input/SelectBox';
import { SensorType } from '@packages/types/device/lite/sensors';
import { CF_1SeriesType, CF_LPCType } from '@packages/types/device/lite/cf_lel';
import {
  getSensorTypeInfo,
  getLiteSensorDefaultValues,
} from '@packages/types/device/lite/lite_constraints';
import { MiniSensorType } from '@packages/types/device/mini/sensors';
import { getMiniSensorInfo } from '@packages/types/device/mini/mini_constraints';
import { formatDueDateFull } from '@/utils/date/kst';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { DeviceType } from '@packages/types/device/common/deviceTypes';

type Option = {
  label: string;
  value: number | string;
};

interface SensorConfigCardProps {
  sensorData: Record<string, unknown>;
  originalData: Record<string, unknown>;
  constraint: Record<string, unknown>;
  isEdit: boolean;
  isSkeleton: boolean;
  deviceType?: DeviceType;
  onClick?: (index: number) => void;
  onUpdate: (updated: Record<string, unknown>) => void;
  errors?: Record<string, string>;
  hints?: Record<string, string>;
  calibrationDueDate?: number | string | null;
  calibrationInterval?: number;
  decimalPoint?: number;
  isClickable?: boolean;
}

const StyledCard = styled(BaseCard)<{
  isEdit: boolean;
  isClickable: boolean;
  onPointerDownCapture?: (e: React.PointerEvent<HTMLDivElement>) => void;
  onClickCapture?: (e: React.MouseEvent<HTMLDivElement>) => void;
}>`
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
  width: auto;
  flex: 1;
  border: 1px solid ${({ theme }) => theme.colors.border.container};
  transition: all 0.2s ease-in-out;

  ${({ isEdit, theme }) =>
    isEdit &&
    `
    border-color: ${theme.colors.border.interactive.selected};
    background-color: ${theme.colors.surface.interactive.hover};
  `}

  ${({ isClickable, theme }) =>
    isClickable &&
    `
    &:hover {
      background-color: ${theme.colors.surface.interactive.hover};
      border-color: ${theme.colors.border.interactive.hover};
      box-shadow: ${theme.shadows.sm};
    }
  `}
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const CalDueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CalDueLabel = styled.span`
  font-weight: 600;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const CalDueValue = styled.span<{ $isOverdue?: boolean }>`
  font-weight: ${props => (props.$isOverdue ? '700' : '500')};
  font-size: 16px;
  color: ${props =>
    props.$isOverdue
      ? props.theme.colors.status.danger.stroke.border
      : props.theme.colors.text.primary};
`;

const DisabledNotice = styled.p`
  margin: 0 0 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SensorFieldsGrid = styled.div<{ deviceType?: DeviceType }>`
  display: ${props => (props.deviceType === DeviceType.MINI ? 'grid' : 'block')};
  grid-template-columns: ${props =>
    props.deviceType === DeviceType.MINI ? 'repeat(4, 1fr)' : 'none'};
  grid-auto-flow: ${props => (props.deviceType === DeviceType.MINI ? 'column' : 'row')};
  grid-template-rows: ${props =>
    props.deviceType === DeviceType.MINI ? 'repeat(3, auto)' : 'auto'};
  width: 100%;
  column-gap: 0;
  row-gap: 0;
  position: relative;

  /* MINI 전용: 좌/우 여백 + divider */
  ${props =>
    props.deviceType === DeviceType.MINI &&
    `
    & > * {
      padding: 0 ${props.theme.spacing[6]}px;
      position: relative;
    }

    /* 첫 번째 열 (1, 2, 3번째 아이템): 왼쪽 패딩 제거 */
    & > *:nth-of-type(1),
    & > *:nth-of-type(2),
    & > *:nth-of-type(3) {
      padding-left: 0;
    }

    /* 마지막 열 (10, 11, 12번째 아이템): 오른쪽 패딩 제거 */
    & > *:nth-of-type(10),
    & > *:nth-of-type(11),
    & > *:nth-of-type(12) {
      padding-right: 0;
    }

    /* 디바이더를 ::after로 추가 (마지막 열 제외) */
    & > *:not(:nth-of-type(10)):not(:nth-of-type(11)):not(:nth-of-type(12))::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 0.5px;
      background: ${(props: { theme: any }) => props.theme.colors.border.divider};
    }
  `}

  /* 반응형: xl 이하에서 2열로 변경 (MINI만) */
  ${props =>
    props.deviceType === DeviceType.MINI &&
    media.maxXl(`
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: repeat(6, auto);

      & > *:nth-of-type(odd) {
        padding-left: 0;
      }

      & > *:nth-of-type(even) {
        padding-right: 0;
      }

      & > *::after {
        display: none;
      }
      
      & > *:nth-of-type(odd):not(:last-child)::after {
        display: block;
        right: 0;
      }
    `)}

  /* 반응형: sm 이하에서 1열로 변경 (MINI만) */
  ${props =>
    props.deviceType === DeviceType.MINI &&
    media.maxSm(`
      grid-template-columns: 1fr;
      grid-auto-flow: row;
      grid-template-rows: auto;

      & > * {
        padding-left: 0;
        padding-right: 0;
      }

      & > *::after {
        display: none;
      }
    `)}
  }
`;

const FACTOR = 10000;

const getNumericValue = (val: unknown): number => {
  if (typeof val === 'object' && val !== null && 'value' in val) {
    return (val as { value: number }).value;
  }
  return typeof val === 'number' ? val : 0;
};

/**
 * lel_CF_Index 값이 Custom인지 판별한다.
 * label 비교를 우선하되, 파서 폴백('Gas 7' 등)에도 대응하기 위해
 * 센서 타입별 Custom 숫자값도 체크한다.
 * - LEL_LPC: CF_LPCType.CUSTOM = 7
 * - LEL_1Series: CF_1SeriesType.CUSTOM = 9
 */
const isCustomLel = (val: unknown, sensorTypeValue: number): boolean => {
  if (typeof val === 'object' && val !== null && 'label' in val) {
    if ((val as { label: string }).label === 'Custom') return true;
  }

  let numVal: number | undefined;
  if (typeof val === 'number') {
    numVal = val;
  } else if (typeof val === 'object' && val !== null && 'value' in val) {
    numVal = (val as { value: number }).value;
  }

  if (numVal === undefined) return false;

  if (sensorTypeValue === SensorType.LEL_LPC) return numVal === CF_LPCType.CUSTOM;
  if (sensorTypeValue === SensorType.LEL_1Series) return numVal === CF_1SeriesType.CUSTOM;
  return false;
};

export const SensorConfigCard: React.FC<SensorConfigCardProps> = ({
  sensorData,
  constraint,
  isEdit,
  isSkeleton,
  deviceType = DeviceType.LITE,
  onClick,
  onUpdate,
  errors = {},
  hints = {},
  calibrationDueDate,
  calibrationInterval,
  decimalPoint,
  isClickable,
}) => {
  const clickable = isClickable !== undefined ? isClickable : !!onClick;
  const { t } = useTranslation();

  const sensorIndex = sensorData.index as number;
  const getFieldId = (fieldName: string) => `sensor-${sensorIndex}-${fieldName}`;

  // Custom이 아닐 때 저장된 custom_lel 값을 보존하기 위한 ref
  const customLelBackupRef = React.useRef<Record<number, number>>({});

  const [focusedField, setFocusedField] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit) {
      setFocusedField(null);
    }
  }, [isEdit]);

  const handleChange = (key: string, value: unknown) => {
    if (key === 'sensor_disabled') {
      if (value === true) {
        // 비활성화 시 나머지 설정을 센서 기본값으로 리셋한다.
        // 그렇지 않으면 "비활성화 → 활성화 → 임의 입력 → 재비활성화" 흐름에서
        // 검증되지 않은 값이 sensor_disabled=true 상태로 그대로 저장될 수 있다.
        const defaults = getLiteSensorDefaultValues(sensorType as SensorType);
        onUpdate({
          sensor_disabled: true,
          gas_conc_unit: defaults.gas_conc_unit,
          calibration_gas_conc: defaults.calibration_gas_conc,
          calibration_interval: defaults.calibration_interval,
          bump_interval: defaults.bump_interval,
          low_alarm: defaults.low_alarm,
          high_alarm: defaults.high_alarm,
          twa_alarm: defaults.twa_alarm,
          stel_alarm: defaults.stel_alarm,
          twa_period: defaults.twa_period,
          stel_period: defaults.stel_period,
          custom_lel: defaults.custom_lel,
          lel_CF_Index: defaults.lel_CF_Index,
          vol_for_50_lel_ch4: defaults.vol_for_50_lel_ch4,
          bump_test_threshold: defaults.bump_test_threshold,
        });
      } else {
        onUpdate({ sensor_disabled: false });
      }
      return;
    }

    if (key === 'lel_CF_Index') {
      const updateData: Record<string, unknown> = { lel_CF_Index: value };

      const isCustom =
        typeof value === 'object' && value !== null && 'label' in value
          ? (value as { label: string }).label === 'Custom'
          : false;

      const currentCustomLel = sensorData.custom_lel as number | undefined;

      if (isCustom) {
        const backup = customLelBackupRef.current[sensorIndex];
        updateData.custom_lel = backup !== undefined ? backup : 1;
        delete customLelBackupRef.current[sensorIndex];
      } else {
        if (currentCustomLel !== undefined && currentCustomLel !== 1) {
          customLelBackupRef.current[sensorIndex] = currentCustomLel;
        }
        updateData.custom_lel = 1;
      }

      onUpdate(updateData);
      return;
    }

    onUpdate({ [key]: value });
  };

  const handleUnitChange = (selected: Option) => {
    const prevUnit = (sensorData.gas_conc_unit as Option)?.value as number;
    const newUnit = selected.value as number;
    if (prevUnit === newUnit) return;

    const convert = (val: number) => (newUnit === 1 ? val / FACTOR : val * FACTOR);

    onUpdate({
      gas_conc_unit: selected,
      calibration_gas_conc: convert(sensorData.calibration_gas_conc as number),
      low_alarm: convert(sensorData.low_alarm as number),
      high_alarm: convert(sensorData.high_alarm as number),
      twa_alarm: convert(sensorData.twa_alarm as number),
      stel_alarm: convert(sensorData.stel_alarm as number),
      custom_lel: convert(sensorData.custom_lel as number),
    });
  };

  // 센서 타입 확인
  const sensorType = (sensorData.type as Option)?.value as number;
  const sensorTypeString = (sensorData.type as Option)?.label as string;

  const isMiniDevice = deviceType === DeviceType.MINI;

  // 센서 비활성화 상태 (MINI는 해당 토글이 없음): 비활성화 시 다른 설정값 편집이 불필요하므로 잠금 처리
  const isSensorDisabled = !isMiniDevice && !!sensorData.sensor_disabled;

  // 센서 정보 가져오기
  let sensorInfo;
  if (isMiniDevice) {
    const miniSensorTypeMap: Record<string, MiniSensorType> = {
      CO: MiniSensorType.CO,
      O2: MiniSensorType.O2,
      H2: MiniSensorType.H2,
      H2S: MiniSensorType.H2S,
      NH3: MiniSensorType.NH3,
      Cl2: MiniSensorType.Cl2,
      SO2: MiniSensorType.SO2,
      NO2: MiniSensorType.NO2,
      HCN: MiniSensorType.HCN,
      PH3: MiniSensorType.PH3,
      ETO: MiniSensorType.ETO,
      O3: MiniSensorType.O3,
      HCl: MiniSensorType.HCl,
      NO: MiniSensorType.NO,
      'NH3-E': MiniSensorType.NH3_E,
      'O2-LF': MiniSensorType.O2_LF,
      'CO-H': MiniSensorType.CO_H,
      CH2O2: MiniSensorType.CH2O2,
      'CO-E': MiniSensorType.CO_E,
    };
    const miniSensorType = miniSensorTypeMap[sensorTypeString] || MiniSensorType.CO;
    sensorInfo = getMiniSensorInfo(miniSensorType);
  } else {
    sensorInfo = getSensorTypeInfo(sensorType as SensorType) || getSensorTypeInfo(SensorType.O2LF);
  }

  const supportsTwaStel = sensorInfo?.has_twa_stel || false;
  const supportsAutoZero = sensorInfo?.enable_auto_fresh || false;

  const supportsCorrectionFactor = isMiniDevice
    ? false
    : (sensorInfo as { enable_correction_factor?: boolean })?.enable_correction_factor || false;
  const shouldShowLelCfIndex = supportsCorrectionFactor && sensorType !== SensorType.LEL_IR;

  const shouldShowVolFor50Lel = isMiniDevice
    ? false
    : supportsCorrectionFactor || sensorType === SensorType.LEL_IR;
  const supportsUnitSelection = isMiniDevice
    ? false
    : (sensorInfo as { enable_unit_selection?: boolean })?.enable_unit_selection || false;

  if (isSkeleton) {
    return (
      <StyledCard isEdit={false} isClickable={false}>
        <CardTitle>Loading...</CardTitle>
        <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>
          Loading sensor data...
        </div>
      </StyledCard>
    );
  }

  const { text: calDueText, isOverdue: calDueOverdue } =
    calibrationInterval === 0
      ? { text: '-', isOverdue: false }
      : formatDueDateFull(calibrationDueDate);

  return (
    <StyledCard
      isEdit={isEdit}
      isClickable={clickable}
      onPointerDownCapture={(e: React.PointerEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget || (e.target as HTMLElement).tagName === 'H3') {
          setFocusedField(null);
        }
        onClick?.(sensorData.index as number);
      }}
      onClickCapture={(e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget || (e.target as HTMLElement).tagName === 'H3') {
          setFocusedField(null);
        }
        onClick?.(sensorData.index as number);
      }}
    >
      <TitleRow>
        <CardTitle>
          {(sensorData.name as string) ||
            (sensorData.type as Option)?.label ||
            `Sensor ${sensorIndex + 1}`}
        </CardTitle>
        {calibrationDueDate !== undefined && calibrationDueDate !== null && (
          <CalDueContainer>
            <CalDueLabel>Cal Due:</CalDueLabel>
            <CalDueValue $isOverdue={calDueOverdue}>{calDueText}</CalDueValue>
          </CalDueContainer>
        )}
      </TitleRow>

      <SensorFieldsGrid deviceType={deviceType}>
        {isSensorDisabled && isEdit && (
          <DisabledNotice>{t(i18nKeys.config.msg_sensorDisabledFieldsLocked)}</DisabledNotice>
        )}
        <NumberInput
          label={t(i18nKeys.config.calibrationGasConcentration)}
          value={sensorData.calibration_gas_conc as number}
          onChange={val => handleChange('calibration_gas_conc', val)}
          isEdit={isEdit}
          disabled={isSensorDisabled}
          unit={(sensorData.gas_conc_unit as Option)?.label as string}
          error={errors.calibration_gas_conc}
          hint={hints.calibration_gas_conc}
          isFocused={focusedField === 'calibration_gas_conc'}
          onFocusChange={focused => setFocusedField(focused ? 'calibration_gas_conc' : null)}
          id={getFieldId('calibration-gas-concentration')}
          name="calibration_gas_conc"
          decimalPoint={decimalPoint}
        />
        <NumberInput
          label={t(i18nKeys.config.calibrationInterval)}
          value={sensorData.calibration_interval as number}
          onChange={val => handleChange('calibration_interval', val)}
          isEdit={isEdit}
          disabled={isSensorDisabled}
          unit={(constraint.calibration_interval as { unit?: string })?.unit as string}
          error={errors.calibration_interval}
          hint={hints.calibration_interval}
          isFocused={focusedField === 'calibration_interval'}
          onFocusChange={focused => setFocusedField(focused ? 'calibration_interval' : null)}
          id={getFieldId('calibration-interval')}
          name="calibration_interval"
          step={1}
        />
        <NumberInput
          label={t(i18nKeys.config.bumpInterval)}
          value={sensorData.bump_interval as number}
          onChange={val => handleChange('bump_interval', val)}
          isEdit={isEdit}
          disabled={isSensorDisabled}
          unit={(constraint.bump_interval as { unit?: string })?.unit}
          error={errors.bump_interval}
          hint={hints.bump_interval}
          isFocused={focusedField === 'bump_interval'}
          onFocusChange={focused => setFocusedField(focused ? 'bump_interval' : null)}
          id={getFieldId('bump-interval')}
          name="bump_interval"
          step={1}
        />
        {/* Bump Test Threshold - O2/O2LF는 40으로 고정되어 숨김 */}
        {!isMiniDevice && sensorInfo?.enable_bump_test_threshold && (
          <NumberInput
            label={t(i18nKeys.config.bumpTestThreshold)}
            value={sensorData.bump_test_threshold as number}
            onChange={val => handleChange('bump_test_threshold', val)}
            isEdit={isEdit}
            disabled={isSensorDisabled}
            unit={(constraint.bump_test_threshold as { unit?: string })?.unit}
            error={errors.bump_test_threshold}
            hint={hints.bump_test_threshold}
            isFocused={focusedField === 'bump_test_threshold'}
            onFocusChange={focused => setFocusedField(focused ? 'bump_test_threshold' : null)}
            id={getFieldId('bump-test-threshold')}
            name="bump_test_threshold"
            step={1}
          />
        )}
        <NumberInput
          label={t(i18nKeys.config.lowAlarm)}
          value={sensorData.low_alarm as number}
          onChange={val => handleChange('low_alarm', val)}
          isEdit={isEdit}
          disabled={isSensorDisabled}
          unit={(sensorData.gas_conc_unit as Option)?.label as string}
          error={errors.low_alarm}
          hint={hints.low_alarm}
          isFocused={focusedField === 'low_alarm'}
          onFocusChange={focused => setFocusedField(focused ? 'low_alarm' : null)}
          id={getFieldId('low-alarm')}
          name="low_alarm"
          decimalPoint={decimalPoint}
        />
        <NumberInput
          label={t(i18nKeys.config.highAlarm)}
          value={sensorData.high_alarm as number}
          onChange={val => handleChange('high_alarm', val)}
          isEdit={isEdit}
          disabled={isSensorDisabled}
          unit={(sensorData.gas_conc_unit as Option)?.label as string}
          error={errors.high_alarm}
          hint={hints.high_alarm}
          isFocused={focusedField === 'high_alarm'}
          onFocusChange={focused => setFocusedField(focused ? 'high_alarm' : null)}
          id={getFieldId('high-alarm')}
          name="high_alarm"
          decimalPoint={decimalPoint}
        />
        {supportsTwaStel && (
          <>
            <NumberInput
              label={t(i18nKeys.config.twaAlarm)}
              value={sensorData.twa_alarm as number}
              onChange={val => handleChange('twa_alarm', val)}
              isEdit={isEdit}
              disabled={isSensorDisabled}
              unit={(sensorData.gas_conc_unit as Option)?.label as string}
              error={errors.twa_alarm}
              hint={hints.twa_alarm}
              isFocused={focusedField === 'twa_alarm'}
              onFocusChange={focused => setFocusedField(focused ? 'twa_alarm' : null)}
              id={getFieldId('twa-alarm')}
              name="twa_alarm"
              decimalPoint={decimalPoint}
            />

            <NumberInput
              label={t(i18nKeys.config.stelAlarm)}
              value={sensorData.stel_alarm as number}
              onChange={val => handleChange('stel_alarm', val)}
              isEdit={isEdit}
              disabled={isSensorDisabled}
              unit={(sensorData.gas_conc_unit as Option)?.label as string}
              error={errors.stel_alarm}
              hint={hints.stel_alarm}
              isFocused={focusedField === 'stel_alarm'}
              onFocusChange={focused => setFocusedField(focused ? 'stel_alarm' : null)}
              id={getFieldId('stel-alarm')}
              name="stel_alarm"
              decimalPoint={decimalPoint}
            />

            <NumberInput
              label={t(i18nKeys.config.twaPeriod)}
              value={sensorData.twa_period as number}
              onChange={val => handleChange('twa_period', val)}
              isEdit={isEdit}
              disabled={isSensorDisabled}
              unit={(constraint.twa_period as { unit?: string })?.unit}
              error={errors.twa_period}
              hint={hints.twa_period}
              isFocused={focusedField === 'twa_period'}
              onFocusChange={focused => setFocusedField(focused ? 'twa_period' : null)}
              id={getFieldId('twa-period')}
              name="twa_period"
              step={1}
            />

            <NumberInput
              label={t(i18nKeys.config.stelPeriod)}
              value={sensorData.stel_period as number}
              onChange={val => handleChange('stel_period', val)}
              isEdit={isEdit}
              disabled={isSensorDisabled}
              unit={(constraint.stel_period as { unit?: string })?.unit}
              error={errors.stel_period}
              hint={hints.stel_period}
              isFocused={focusedField === 'stel_period'}
              onFocusChange={focused => setFocusedField(focused ? 'stel_period' : null)}
              id={getFieldId('stel-period')}
              name="stel_period"
              step={1}
            />
          </>
        )}
        {shouldShowLelCfIndex && (
          <SelectBox
            label={t(i18nKeys.config.lel_CF_Index)}
            isEdit={isEdit}
            disabled={isSensorDisabled}
            value={getNumericValue(sensorData.lel_CF_Index)}
            options={(constraint.lel_CF_Index as Option[]) || []}
            onChange={selected => handleChange('lel_CF_Index', selected)}
            id={getFieldId('lel_CF_Index')}
            name="lel_CF_Index"
          />
        )}
        {shouldShowLelCfIndex && isCustomLel(sensorData.lel_CF_Index, sensorType) && (
          <NumberInput
            label={t(i18nKeys.config.correctionFactor)}
            value={(sensorData.custom_lel as number) || 1}
            onChange={val => handleChange('custom_lel', val)}
            isEdit={isEdit}
            disabled={isSensorDisabled}
            unit=""
            error={errors.custom_lel}
            hint={hints.custom_lel}
            isFocused={focusedField === 'custom_lel'}
            onFocusChange={focused => setFocusedField(focused ? 'custom_lel' : null)}
            id={getFieldId('custom-lel')}
            name="custom_lel"
          />
        )}
        {shouldShowVolFor50Lel && (
          <SelectBox
            label={t(i18nKeys.config.vol_for_50_lel_ch4)}
            isEdit={isEdit}
            disabled={isSensorDisabled}
            value={getNumericValue(sensorData.vol_for_50_lel_ch4)}
            options={(constraint.vol_for_50_lel_ch4 as Option[]) || []}
            onChange={selected => handleChange('vol_for_50_lel_ch4', selected)}
            id={getFieldId('vol_for_50_lel_ch4')}
            name="vol_for_50_lel_ch4"
          />
        )}
        {supportsUnitSelection && (
          <SelectBox
            label={t(i18nKeys.config.gasUnit)}
            isEdit={isEdit}
            disabled={isSensorDisabled}
            value={(sensorData.gas_conc_unit as Option)?.value as number}
            options={(constraint.unit as Option[]) || []}
            onChange={selected => {
              const valueNum =
                typeof selected.value === 'string' ? Number(selected.value) : selected.value;
              if (valueNum !== (sensorData.gas_conc_unit as Option)?.value) {
                handleUnitChange({ ...selected, value: valueNum });
              }
            }}
            id={getFieldId('gas-concentration-unit')}
            name="gas_conc_unit"
          />
        )}{' '}
        {isMiniDevice && supportsAutoZero && (
          <Toggle
            label={t(i18nKeys.config.autoZeroOnStartup)}
            value={!!sensorData.auto_fresh_air_cal}
            onChange={val => handleChange('auto_fresh_air_cal', val)}
            isEdit={isEdit}
            id={getFieldId('auto-zero-on-startup')}
            name="auto_fresh_air_cal"
          />
        )}
        {!isMiniDevice && supportsAutoZero && (
          <Toggle
            label={t(i18nKeys.config.autoFreshAirCal)}
            value={!!sensorData.auto_fresh_air_cal}
            onChange={val => handleChange('auto_fresh_air_cal', val)}
            isEdit={isEdit}
            disabled={isSensorDisabled}
            id={getFieldId('auto-fresh-air-cal')}
            name="auto_fresh_air_cal"
          />
        )}
        {!isMiniDevice && (
          <Toggle
            label={t(i18nKeys.config.sensorDisable)}
            value={!!sensorData.sensor_disabled}
            onChange={val => handleChange('sensor_disabled', val)}
            isEdit={isEdit}
            id={getFieldId('sensor-disabled')}
            name="sensor_disabled"
          />
        )}
      </SensorFieldsGrid>
    </StyledCard>
  );
};
